/**
 * Organization Roles Hook
 * 
 * Manages role-based access control for organization members. This hook enables
 * organization owners to view and update member roles, controlling what actions
 * each member can perform within the organization.
 * 
 * **Role Hierarchy:**
 * - **Owner**: Full control, can manage all members and settings
 * - **Super User**: Advanced permissions, can manage most operations
 * - **Member**: Standard access, limited to basic operations
 * 
 * **Key Features:**
 * - Fetch all member roles for an organization
 * - Update member roles (owner-only operation)
 * - Real-time role validation
 * - Automatic cache invalidation on role changes
 * 
 * **Security:**
 * - Only organization owners can update roles
 * - Role changes are tracked with timestamps
 * - Prevents unauthorized role escalation
 * 
 * **Use Cases:**
 * - Member management interfaces
 * - Role assignment workflows
 * - Access control dashboards
 * - Audit trails for role changes
 * 
 * @param {string} organizationId - The organization ID to fetch roles for
 * 
 * @returns {Object} Organization roles state
 * @returns {OrgRole[]} roles - Array of member roles
 * @returns {boolean} isLoading - Loading state
 * @returns {Error | null} error - Error if fetch failed
 * @returns {Function} updateRole - Update a member's role
 * @returns {boolean} isUpdating - Whether an update is in progress
 * 
 * @example
 * // Manage organization roles
 * const { roles, updateRole, isLoading } = useOrganizationRoles(orgId);
 * 
 * // Update member role
 * updateRole({
 *   userId: 'user-123',
 *   newRole: 'super_user'
 * });
 * 
 * @example
 * // Display member list with role badges
 * {roles?.map(role => (
 *   <MemberCard key={role.id}>
 *     <RoleBadge role={role.org_role} />
 *     <RoleSelector
 *       currentRole={role.org_role}
 *       onChange={(newRole) => updateRole({
 *         userId: role.user_id,
 *         newRole
 *       })}
 *     />
 *   </MemberCard>
 * ))}
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface OrgRole {
  id: string;
  user_id: string;
  organization_id: string;
  org_role: 'owner' | 'super_user' | 'member';
  created_at: string;
  updated_at: string;
}

export const useOrganizationRoles = (organizationId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all member roles
  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['org-roles', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_user_roles' as any)
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at');
      
      if (error) throw error;
      return data as unknown as OrgRole[];
    },
    enabled: !!organizationId,
  });

  // Update member role (owner only)
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'owner' | 'super_user' | 'member' }) => {
      const { error } = await supabase
        .from('organization_user_roles' as any)
        .update({ 
          org_role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('organization_id', organizationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-roles', organizationId] });
      toast({
        title: "Role Updated",
        description: "Member role has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update member role.",
        variant: "destructive",
      });
    },
  });

  return { 
    roles, 
    isLoading, 
    error,
    updateRole: updateRoleMutation.mutate,
    isUpdating: updateRoleMutation.isPending,
  };
};
